/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { useAppContext } from '../../common/ui/app-context';
import { useBootstrap } from '../../controllers/store/bootstrap';

import { LiteAppFilterBar, LiteShowAllAppsButton } from './filters/lite-app-filter-bar';

/**
 * Minimum width (in px) required for the filters section in combined row mode.
 * This accounts for the dropdown toggle button (~40px) plus at least one inline
 * filter (~200px) and some padding.
 */
const MIN_FILTERS_SECTION_WIDTH = 300;

/**
 * Estimated width (in px) per app pill in the app filter bar. Each pill is a
 * button containing an icon + label, typically 80–120px. We use a conservative
 * estimate to ensure we don't show the combined row when there isn't enough
 * space.
 */
const ESTIMATED_APP_PILL_WIDTH = 110;

/**
 * Fixed overhead in the app filter bar (in px): the "All" pill (~50px), the
 * "Show all apps" button (~120px when visible), gaps between pills (~12px each),
 * and the grid column gap between the app bar and filters section (16px).
 */
const APP_FILTER_BAR_FIXED_OVERHEAD = 200;

type LiteHeaderProps = {
	input: React.ReactNode;
	filters: React.ReactNode;
};

const styles = cssMap({
	base: {
		position: 'sticky',
		top: '0',
		backgroundColor: token('elevation.surface'),
		width: '100%',
		backgroundClip: 'padding-box',
		zIndex: 200,
	},
	contentContainer: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	keyline: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	gridCollapsed: {
		gridTemplateAreas: '"input ." "appFilterBar toggleBtn"',
		gridTemplateColumns: '1fr auto',
		paddingBottom: token('space.250'),
	},
	gridExpanded: {
		gridTemplateAreas: '"input ." "appFilterBar appFilterBar" "filters toggleBtn"',
		gridTemplateColumns: '1fr auto',
		paddingBottom: token('space.250'),
	},
	inputGridArea: {
		gridArea: 'input',
		display: 'flex',
		alignItems: 'center',
	},
	appFilterBarGridArea: {
		gridArea: 'appFilterBar',
		minWidth: '0px',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
	appFilterBarScroll: {
		minWidth: '0px',
		flex: '1 1 0',
	},
	toggleBtnGridArea: {
		gridArea: 'toggleBtn',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: token('space.150'),
	},
	filtersGridArea: {
		gridArea: 'filters',
	},
	gridSingleApp: {
		gridTemplateAreas: '"input ." "filters filters"',
		gridTemplateColumns: '1fr auto',
		paddingBottom: token('space.250'),
	},
	gridCombinedRow: {
		gridTemplateAreas: '"input . ." "appFilterBar filtersDivider filters"',
		gridTemplateColumns: 'auto auto 1fr',
		columnGap: token('space.150'),
		paddingBottom: token('space.250'),
	},
	paddingTop: {
		paddingTop: token('space.300'),
	},
	filtersDivider: {
		gridArea: 'filtersDivider',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRightStyle: 'solid',
		alignSelf: 'stretch',
		marginBlock: token('space.100'),
	},
});

export const LiteHeader = ({ input, filters }: LiteHeaderProps): React.JSX.Element => {
	const { isNav4Enabled } = useAppContext();
	const { formatMessage } = useIntl();
	const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
	const [{ products }] = useBootstrap();

	const isSingleApp = products.length <= 1;

	// Track the content container width via ResizeObserver. The content
	// container is always in the DOM regardless of layout mode, so we get
	// reliable width updates even when the filters element is unmounted
	// (2-row collapsed). This avoids the chicken-and-egg problem where
	// the filters element must be rendered to be measured.
	const [containerWidth, setContainerWidth] = useState(0);
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setContainerWidth(entry.contentRect.width);
			}
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
		containerRef.current = node;
		if (node) {
			setContainerWidth(node.getBoundingClientRect().width);
		}
	}, []);

	// Estimate the app filter bar's natural width based on the number of
	// connected products. We can't reliably measure the bar's intrinsic width
	// because in the 2-row grid layout it stretches to fill 1fr, and
	// scrollWidth reports the stretched size. Using a per-pill estimate avoids
	// this measurement problem while adapting to the number of connected apps.
	const estimatedAppBarWidth =
		products.length * ESTIMATED_APP_PILL_WIDTH + APP_FILTER_BAR_FIXED_OVERHEAD;

	// Determine whether there's enough remaining space in the container for
	// the filters section alongside the app filter bar.
	// When containerWidth is 0 (not yet measured), default to combined row
	// so the layout is immediately correct on wide viewports.
	const remainingWidth = containerWidth - estimatedAppBarWidth;
	const hasSufficientWidth = containerWidth === 0 || remainingWidth >= MIN_FILTERS_SECTION_WIDTH;
	const isCombinedRow = !isSingleApp && hasSufficientWidth;

	return (
		<>
			<Box xcss={cx(styles.base, styles.keyline)} testId="search-page-header">
				<Box xcss={styles.contentContainer} ref={containerRefCallback}>
					{isSingleApp ? (
						// Single app: no app filter bar, just show filters directly
						<Grid
							xcss={cx(styles.gridSingleApp, !isNav4Enabled && styles.paddingTop)}
							columnGap="space.200"
							rowGap="space.200"
						>
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<Box xcss={styles.filtersGridArea}>{filters}</Box>
						</Grid>
					) : isCombinedRow ? (
						// Combined row: app filter bar and filters share the same row
						<Grid
							xcss={cx(styles.gridCombinedRow, !isNav4Enabled && styles.paddingTop)}
							rowGap="space.200"
							testId="lite-header-combined-row"
						>
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<Box xcss={styles.appFilterBarGridArea}>
								<Box xcss={styles.appFilterBarScroll}>
									<LiteAppFilterBar />
								</Box>
								<LiteShowAllAppsButton />
							</Box>
							<Box xcss={styles.filtersDivider} />
							<Box xcss={styles.filtersGridArea}>{filters}</Box>
						</Grid>
					) : (
						// Default 2-row layout: app filter bar on row 1, filters (expandable) on row 2
						<Grid
							xcss={cx(
								isFiltersExpanded ? styles.gridExpanded : styles.gridCollapsed,
								!isNav4Enabled && styles.paddingTop,
							)}
							columnGap="space.200"
							rowGap="space.200"
						>
							{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
							<Box xcss={styles.appFilterBarGridArea}>
								<Box xcss={styles.appFilterBarScroll}>
									<LiteAppFilterBar />
								</Box>
								<LiteShowAllAppsButton />
							</Box>
							{isFiltersExpanded && <Box xcss={styles.filtersGridArea}>{filters}</Box>}
							<Box xcss={styles.toggleBtnGridArea}>
								<Button
									appearance="default"
									iconBefore={isFiltersExpanded ? ChevronUpIcon : ChevronDownIcon}
									onClick={() => setIsFiltersExpanded((prev) => !prev)}
								>
									{isFiltersExpanded
										? formatMessage({
												id: 'search_page.header.less_filters',
												defaultMessage: 'Less filters',
												description:
													'Button to collapse the filters section in the search page header',
											})
										: formatMessage({
												id: 'search_page.header.more_filters',
												defaultMessage: 'More filters',
												description:
													'Button to expand the filters section in the search page header',
											})}
								</Button>
							</Box>
						</Grid>
					)}
				</Box>
			</Box>
			{isNav4Enabled && <VisuallyHidden>{input}</VisuallyHidden>}
		</>
	);
};
